
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ProfileFormState, Profile, SocialPlatform } from '../types/profileTypes';

const PROFILE_FORM_STORAGE_KEY = 'creator_profile_form_state';

export const useProfileFormState = (profile: Profile | null) => {
  const { user } = useAuth();
  const [profileFormState, setProfileFormState] = useState<ProfileFormState | null>(null);

  // Initialize form state when profile data is available
  useEffect(() => {
    if (profile) {
      const savedFormState = localStorage.getItem(PROFILE_FORM_STORAGE_KEY);
      
      if (savedFormState) {
        try {
          const parsedState = JSON.parse(savedFormState);
          console.log('Restored profile form state from localStorage', parsedState);
          setProfileFormState(parsedState);
          localStorage.removeItem(PROFILE_FORM_STORAGE_KEY);
        } catch (e) {
          console.error('Error parsing saved form state', e);
          initializeFormStateFromProfile(profile);
        }
      } else {
        initializeFormStateFromProfile(profile);
      }
    }
  }, [profile]);

  // Save form state to local storage when page visibility changes or before unload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && profileFormState) {
        localStorage.setItem(PROFILE_FORM_STORAGE_KEY, JSON.stringify(profileFormState));
        console.log('Profile form state saved to localStorage');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const handleBeforeUnload = () => {
      if (profileFormState) {
        localStorage.setItem(PROFILE_FORM_STORAGE_KEY, JSON.stringify(profileFormState));
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [profileFormState]);

  const initializeFormStateFromProfile = (profile: Profile) => {
    const socialPlatforms: SocialPlatform[] = ["tiktok", "instagram", "youtube", "linkedin", "twitter", "facebook", "website"];
    
    const extractUsernameFromUrl = (url: string): string => {
      if (!url) return "";
      
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/').filter(Boolean);
        
        return pathParts.length > 0 ? pathParts[pathParts.length - 1] : "";
      } catch (e) {
        return url;
      }
    };

    setProfileFormState({
      name: profile.full_name || "",
      username: profile.username || "",
      bio: profile.bio || "",
      expressions: profile.expression_types || ["edit"],
      socialProfiles: socialPlatforms.map(platform => {
        const existingUrl = profile.social_links?.[platform] || "";
        return {
          platform,
          url: existingUrl,
          username: extractUsernameFromUrl(existingUrl)
        };
      })
    });
  };

  const handleFormStateChange = (newState: Partial<ProfileFormState>) => {
    setProfileFormState(prev => prev ? { ...prev, ...newState } : null);
  };

  return {
    profileFormState,
    handleFormStateChange
  };
};
